import { GroupeCreateSchema, GroupeFormData, GroupeUpdateSchema } from "@repo/types";
import {
  useCreateGroupe,
  useGroupe,
  useUpdateGroupe,
} from "@repo/ui/hooks/api/index";
import { useSubscriptionConfig } from "@repo/ui/hooks/api/settings/use-settings";
import { produce } from "immer";
import { useEffect, useState } from "react";

type PlanType = "BASIC" | "ADVANCED";

const NUMERIC_FIELDS: ReadonlyArray<keyof GroupeFormData> = [
  "licence",
  "forfait_id",
  "max_parcours_per_month",
  "current_month_parcours",
];

const DEFAULT_FORM_DATA: GroupeFormData = {
  name: "",
  groupe_type: "DENDROMAP",
  licence: 1,
  forfait_id: 1,
  is_init: true,
  is_stripped: false,
  plan_type: "BASIC",
  max_parcours_per_month: 10,
  current_month_parcours: 0,
  subscription_status: "ACTIVE",
  tier_id: "basic-10",
};

interface UseGroupeFormProps {
  groupeId?: number;
  onSuccess: () => void;
}

function parseNumericValue(value: string): number | undefined {
  if (value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Form hook for groupe creation/edition.
 *
 * Uses Zod (GroupeCreateSchema / GroupeUpdateSchema) for validation at submit
 * time. We keep a `formData` + handlers surface (rather than full react-hook-form)
 * to preserve the existing section components contract.
 */
export function useGroupeForm({ groupeId, onSuccess }: UseGroupeFormProps) {
  const [formData, setFormData] = useState<GroupeFormData>(DEFAULT_FORM_DATA);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>("BASIC");
  const [selectedTierId, setSelectedTierId] = useState<string>("basic-10");

  const isEditing = !!groupeId;
  const { data: groupeData, isLoading: isLoadingGroupe } = useGroupe(groupeId ?? 0);
  const { data: subscriptionConfig, isLoading: isLoadingSubscription } =
    useSubscriptionConfig();
  const createGroupe = useCreateGroupe();
  const updateGroupe = useUpdateGroupe();

  useEffect(() => {
    if (!isEditing) {
      setFormData(DEFAULT_FORM_DATA);
      setSelectedPlanType("BASIC");
      setSelectedTierId("basic-10");
    }
  }, [isEditing, groupeId]);

  useEffect(() => {
    if (!isEditing || !groupeData?.data) return;
    const source = groupeData.data as Partial<GroupeFormData> & {
      plan_type?: PlanType;
      tier_id?: string;
    };
    const plan: PlanType = source.plan_type ?? "BASIC";
    const tier = source.tier_id ?? "";

    setFormData({
      name: source.name ?? "",
      groupe_type: source.groupe_type ?? "DENDROMAP",
      licence: source.licence ?? 1,
      forfait_id: source.forfait_id ?? 1,
      is_init: source.is_init ?? false,
      is_stripped: source.is_stripped ?? false,
      plan_type: plan,
      max_parcours_per_month: source.max_parcours_per_month ?? 10,
      current_month_parcours: source.current_month_parcours ?? 0,
      subscription_status: source.subscription_status ?? "ACTIVE",
      tier_id: tier,
    });
    setSelectedPlanType(plan);
    setSelectedTierId(tier);
  }, [isEditing, groupeData, groupeId]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const nextValue = (NUMERIC_FIELDS as ReadonlyArray<string>).includes(name)
      ? parseNumericValue(value)
      : value;
    setFormData(
      produce((draft: Record<string, unknown>) => {
        draft[name] = nextValue;
      })
    );
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(
      produce((draft: Record<string, unknown>) => {
        draft[name] = name === "forfait_id" ? Number(value) : value;
      })
    );
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData(
      produce((draft: Record<string, unknown>) => {
        draft[name] = checked;
      })
    );
  };

  const handlePlanSelect = (planType: PlanType, tierId: string) => {
    setSelectedPlanType(planType);
    setSelectedTierId(tierId);
    setFormData(
      produce((draft: Record<string, unknown>) => {
        draft.plan_type = planType;
        draft.tier_id = tierId;
        const selectedTier = subscriptionConfig?.plans[planType]?.tiers?.find(
          (tier: { id: string; maxTrees: number }) => tier.id === tierId
        );
        if (selectedTier) draft.max_parcours_per_month = selectedTier.maxTrees;
      })
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const schema = isEditing ? GroupeUpdateSchema.omit({ id: true }) : GroupeCreateSchema;
    const result = schema.safeParse(formData);
    if (!result.success) {
      const firstIssue = result.error.issues[0];
      setError(firstIssue?.message ?? "Formulaire invalide.");
      return;
    }

    try {
      if (isEditing && groupeId) {
        await updateGroupe.mutateAsync({ id: groupeId, data: result.data });
      } else {
        await createGroupe.mutateAsync(result.data);
      }
      onSuccess();
    } catch (err) {
      setError("Une erreur est survenue lors de l'enregistrement du groupe.");
    }
  };

  const isLoading = (isEditing && isLoadingGroupe) || isLoadingSubscription;
  const isPending = createGroupe.isPending || updateGroupe.isPending;

  return {
    formData,
    error,
    isEditing,
    isLoading,
    isPending,
    subscriptionConfig,
    selectedPlanType,
    selectedTierId,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleSubmit,
    handlePlanSelect,
  };
}
